<template>
  <v-container justify-center class="main-content">
    <v-layout column fill-height justify-center class="wide-screen">
      <v-form v-model="valid" class="create-team">
        <v-layout column justify-center fluid>
          <v-tooltip left color="rgba(74,190,93,1)">
            <template v-slot:activator="{ on, attrs }">
              <v-flex class="avatar" mb-3 v-bind="attrs" v-on="on">
                <v-icon id="upload-file-icon">upload_file</v-icon>
                <div
                  v-if="imgUrl"
                  class="avatar-image"
                  :style="{ backgroundImage: avatarImage }"></div>
                <v-avatar size="150" v-else class="defaultTeamImage">
                  <img :src="icons.defaultTeamImg" />
                </v-avatar>
                <input
                  id="file-avatar"
                  type="file"
                  name="avatar"
                  @change="onFileChange" />
              </v-flex>
            </template>
            <span class="tooltip_text">Upload your team's logo</span>
          </v-tooltip>
          <v-text-field
            dark
            solo-inverted
            prepend-inner-icon="fas fa-users"
            v-model="form.teamName"
            :rules="teamNameRules"
            label="Team Name"
            :validate-on-blur="true"
            clearable
            required></v-text-field>
          <v-text-field
            dark
            solo-inverted
            prepend-inner-icon="fas fa-lock"
            v-model="form.pin"
            type="number"
            :rules="PINrules"
            :error-messages="PINerrorMessages"
            label="Unique 5 Digit PIN"
            counter="5"
            :validate-on-blur="true"
            :maxlength="5"
            clearable
            required></v-text-field>

          <v-select
            dark
            solo-inverted
            :items="teamTypes"
            v-model="form.teamAge"
            label="Team Type"></v-select>
          <v-btn
            class="create-button"
            :disabled="!valid"
            @click="createTeam()"
            rounded
            dark>
            Create
          </v-btn>
        </v-layout>
      </v-form>
    </v-layout>
    <v-dialog
      dark
      v-model="createTeamSuccessDialog"
      max-width="350"
      class="dialog-window"
      persistent>
      <v-card class="player-modal player-message-modal">
        <v-card-title class="justify-center">
          <v-spacer></v-spacer>
          <h2>Congratulations!</h2>
          <v-spacer></v-spacer>
          <v-icon dark @click="closeSuccessDialog()">close</v-icon>
        </v-card-title>
        <v-card-text>
          <p>You have created a brand new team in SoccerPulse</p>
          <p>But a team is nothing without players</p>
          <ul>
            <li>
              Tell your players to download SoccerPulse and request to Join a
              Team
            </li>

            <li>Give them the Join ID (Listed Below)</li>

            <li>Make sure you add all your players BEFORE adding events</li>
          </ul>
          <p>If you forget, team info can also be found in 'Manage Squads'</p>
          <div class="d-flex" style="justify-content: center">
            <span style="display: flex; align-self: center">
              Copy Join ID: &#8194;
            </span>
            <v-btn ref="copyBtn" @click="copyToClipboard" rounded>
              {{ createdTeam && createdTeam.joinId }}&#8194;
              <v-icon>content_copy</v-icon>
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions class="card-actions">
          <v-btn
            color="darken-1"
            rounded
            class="v-btn--active green"
            @click="closeSuccessDialog()">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// import subscriptionApi from "../../api/subscription.api"
import { mapGetters } from "vuex"
import constants from "@/constants.js"
import { createTeamViaRTDB } from "@/api/createTeamViaRTDB.ts"

export default {
  name: "CreateTeam",
  data() {
    return {
      icons: constants.icons,
      valid: false,
      createTeamErrorDialog: false,
      createTeamSuccessDialog: false,
      errMessages: [{ cause: false }],
      coachProfile: {},
      createdTeam: null,
      form: {
        teamName: "",
        teamAge: "U-10",
      },
      teamNameRules: [(v) => !!v || "Team name is required"],
      PINrules: [
        (v) => {
          const pattern = /\d{5}/gm
          return pattern.test(v) || "Please use only numbers"
        },
        (v) => !!v || "PIN is required",
        (v) => (v && v.length === 5) || "PIN must be 5 digit",
      ],
      PINerrorMessages: [],
      teamTypes: [
        "U-10",
        "U-11",
        "U-12",
        "U-13",
        "U-14",
        "U-15",
        "U-16",
        "U-17",
        "U-18",
        "U-19",
        "U-20",
        "U-21",
        "U-23",
        "College",
        "Pro",
      ],
      imgUrl: null,
    }
  },
  computed: {
    showForm() {
      return this.$store.getters["user/isCoach"]
    },
    avatarImage() {
      return `url(${this.imgUrl || "/assets/icons/team_img.png"})`
    },
    currentUserID() {
      return this.$store.state.user.current.uid
    },
    currentSubOwnerId() {
      return this.$store.state.user.profile.payment_info.subscriptionId
    },
    currentSubOwnerEmail() {
      return this.$store.state.user.profile?.payment_info?.owner?.email
    },
    ...mapGetters({
      availableLicenses: "user/availableLicenses",
    }),
    licenseToUse() {
      return this.availableLicenses[0]
    },
    isCoach() {
      return this.$store.getters["user/isCoach"]
    },
  },
  methods: {
    async copyToClipboard() {
      try {
        if (this.createdTeam && this.createdTeam.joinId) {
          await navigator.clipboard.writeText(this.createdTeam.joinId)
        } else {
          throw new Error("No joinId found")
        }
      } catch (error) {
        console.error("Failed to copy text to clipboard", error)
      }
    },
    async createTeam() {
      if (!this.isCoach) {
        this.$toast.error("Only coaches can create teams")
        return
      }
      if (!this.valid) {
        return
      }

      this.$store.commit("setOverlay", true)
      try {
        this.createdTeam = await createTeamViaRTDB({
          teamName: this.form.teamName,
          teamAge: this.form.teamAge,
          pin: this.form.pin,
          imgUrl: this.form.avatar,
          creatorId: this.$store.state.user.current.uid,
          coachName: `${this.$store.state.user.profile.firstName} ${this.$store.state.user.profile.lastName}`,
          license: this.licenseToUse,
        })
        this.createTeamSuccessDialog = true
      } catch (e) {
        this.$store.dispatch("teamList/handleError", e.message)
        this.$store.commit("setOverlay", false)
        const error = e?.message ?? "Error creating a team"
        this.$toast.error(error)
      } finally {
        this.$store.commit("setOverlay", false)
      }
    },
    onFileChange(event) {
      this.readURL(event.target)
    },
    readURL(input) {
      if (input.files && input.files[0]) {
        const file = input.files[0]
        const reader = new FileReader()
        reader.onload = (event) => {
          this.imgUrl = event.target.result
          this.form.avatar = file
        }
        reader.readAsDataURL(input.files[0])
      }
    },
    async closeSuccessDialog() {
      this.$store.commit("setOverlay", true)
      this.createTeamSuccessDialog = false
      await this.$store.dispatch("teamList/selectTeam", this.createdTeam.teamId)
      this.$router.push("/")
      this.$store.commit("setOverlay", false)
    },
    showToastr(type, message) {
      switch (type) {
        case "success":
          this.$toast.success(message, { className: "success-toaster" })
          break
        case "error":
          this.$toast.error(message)
          break
        case "info":
          this.$toast.info(message, { className: "info-toaster" })
          break
        default:
          break
      }
    },
  },
}
</script>

<style lang="sass">
@import '@/assets/sass/globals.sass'
.owner-warning
  white-space: pre-line
  text-align: center

.v-dialog.v-dialog--active.v-dialog--persistent
  min-width: 400px
  .card-actions
    display: flex
    width: 100%
    justify-content: space-evenly
.create-team
  .create-button
    background-color: $bright-green !important
    height: 45px
    width: 150px
    margin: auto

  .v-input__prepend-inner
    margin-right: 10px

.current-sub-info, .updated-sub-info
  margin-top: 1rem
  display: grid
  grid-template-columns: 1fr 1fr
  grid-template-columns: 1fr 1fr
  column-gap: 0.5rem

.avatar
  overflow: hidden
  width: 150px
  align-self: center
  position: relative
  #upload-file-icon
    position: absolute
    bottom: 0
    color: white
  .avatar-image
    max-width: 150px
    background: center center no-repeat
    background-size: cover
    border-radius: 50%
    padding-top: 100%
    position: relative
    width: 100%
    height: 0
    left: 0
    top: 0
  input
    cursor: pointer
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    opacity: 0
.defaultTeamImage
  position: relative
  height: 100%
  min-width: 150px
</style>
